import {publishOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {XCircleFillIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'

import {ResolveConflictsButton} from '@github-ui/mergebox/components/sections/conflicts/ResolveConflictsButton'
import {ResolveConflictsButtonAndDialog} from '@github-ui/mergebox/components/sections/stacks-merge-conflicts-and-rebase-section/ResolveConflictsButtonAndDialog'
import {ForYouItem} from '../shared/ForYouItem'
import {type MergeabilityData, useMergeabilityData} from './use-mergeability-data'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {useOpenMergeBoxPanel} from '../../mergebox/use-go-to-mergebox'
import {OverviewMergeControl} from './shared/OverviewMergeControl'

const RESOLVE_CONFLICTS_ACTION_ID = 'copilot-resolve-conflicts-action'
const RESOLVE_CONFLICTS_PROMPT = 'Resolve the merge conflicts in this pull request'

type MergeConflictsData = MergeabilityData & {
  conflictsSection: MergeabilityData['conflictsSection'] & {
    conflictsCondition: NonNullable<MergeabilityData['conflictsSection']['conflictsCondition']>
  }
}

export function shouldShowMergeConflicts(data: ReturnType<typeof useMergeabilityData>): data is MergeConflictsData {
  const conflictsSection = data?.conflictsSection
  const isConflictState =
    conflictsSection?.sectionStatus === 'HAS_CONFLICTS' || conflictsSection?.sectionStatus === 'HAS_REBASE_CONFLICTS'

  return (
    !!data &&
    !data.isClosedOrMerged &&
    // if the conflits section is stacked but has explicit conflicts, we still want to render this
    // for you item. shouldRender always returns false if the PR is stacked, so we need to check if
    // the PR itself has conflicts with its base branch. Because we don't want to change any mergebox
    // behavior, we make this change here.
    (!!conflictsSection?.shouldRender ||
      (!!conflictsSection?.isStacked && conflictsSection?.sectionStatus === 'HAS_CONFLICTS')) &&
    isConflictState &&
    !!conflictsSection.conflictsCondition
  )
}

function startConflictResolutionAgent() {
  publishOpenCopilotChat({
    id: RESOLVE_CONFLICTS_ACTION_ID,
    intent: CopilotChatIntents.actionsAgent,
    content: RESOLVE_CONFLICTS_PROMPT,
    references: [],
  })
}

// Renders when the branch has merge conflicts that must be resolved
export function MergeConflicts({variant}: {variant?: 'default' | 'embedded'}) {
  const data = useMergeabilityData()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const openMergeBoxPanel = useOpenMergeBoxPanel()

  if (!shouldShowMergeConflicts(data)) return null

  const {mergeBoxPullRequest, conflictsSection, mergeSection, stackSection} = data
  const {sectionStatus, conflictsCondition} = conflictsSection
  const conflictsWebEditorPath = `${mergeBoxPullRequest.resourcePath}/conflicts`
  const showAgentOption = mergeSection.canUserPushToBase && !!mergeBoxPullRequest.viewerCanUseCopilotCodingAgent
  const sendConflictAction = (actionName: 'edit_conflicts_on_web' | 'fix_conflicts_with_copilot') =>
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: 'merge-conflicts',
      action_name: actionName,
    })

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={XCircleFillIcon} variant="danger" emphasized />}
      emphasizedLeadingVisual
      title={'Merge conflicts'}
      itemName="merge-conflicts"
      padding="none"
      variant={variant}
      description={
        sectionStatus === 'HAS_CONFLICTS' ? (
          <span>
            Resolve the{' '}
            <Link as="button" inline onClick={event => openMergeBoxPanel(event.currentTarget)}>
              merge conflicts
            </Link>{' '}
            before this can merge.
          </span>
        ) : undefined
      }
      actionSlot={
        <>
          {conflictsSection.isStacked ? (
            <ResolveConflictsButtonAndDialog stackNumber={stackSection?.stackMetadata?.number ?? 0} size="small" />
          ) : (
            <ResolveConflictsButton
              buttonId={RESOLVE_CONFLICTS_ACTION_ID}
              conflictsWebEditorPath={conflictsWebEditorPath}
              conflictsState={sectionStatus}
              onActionSelect={sendConflictAction}
              onFixWithCopilot={startConflictResolutionAgent}
              showAgentOption={showAgentOption}
              webEditorConflictResolution={conflictsCondition.webEditorConflictResolution}
              size="small"
            />
          )}
          <OverviewMergeControl data={data} itemName="merge-conflicts" />
        </>
      }
    />
  )
}
